import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Divider, Typography, Upload } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ToastVariant } from '../../components/Toast/toast.model';
import { toastService } from '../../components/Toast/toast.service';
import { firebaseStorage, firestore } from '../../firebase/firebase';
import { FirestoreCollection } from '../../firebase/firebase.models';
import { Offer } from '../../models/offers.model';
import { useAuth } from '../Auth/AuthContext';
import { FilePaths } from '../OfferAdd/Documents/documents.models';

const Container = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
`;

const InstructionList = styled.ol`
  padding-left: 20px;
  margin: 20px 0;

  li {
    margin: 10px 0;
    font-size: 16px;
  }
`;

const ButtonContainer = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface Props {
  offerId: string;
  offer: Offer;
  userRole: 'landlord' | 'tenant';
  sourceFileName: FilePaths;
  targetFileName: FilePaths;
  onUploadComplete: (downloadUrl: string) => Promise<void>;
}

export const ManualSignatureUpload: React.FC<Props> = ({
  offerId,
  sourceFileName,
  targetFileName,
  onUploadComplete,
}) => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const getCompanyId = async (): Promise<string> => {
    const userDoc = await firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid).get();
    const userData = userDoc.data();
    return userData?.companyId;
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const companyId = await getCompanyId();

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const downloadUrl = await firebaseStorage
        .ref(`companies/${companyId}/offers/${offerId}/${sourceFileName}`)
        .getDownloadURL();

      // Otwórz w nowej karcie
      window.open(downloadUrl, '_blank');

      toastService.show('Dokument został pobrany', 'Sukces', { variant: ToastVariant.SUCCESS });
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      toastService.show('Nie udało się pobrać dokumentu. Spróbuj ponownie lub odśwież stronę.', 'Błąd pobierania', {
        variant: ToastVariant.ERROR,
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toastService.show('Wybierz plik PDF do załączenia', 'Błąd', { variant: ToastVariant.ERROR });
      return;
    }

    setUploading(true);
    try {
      const companyId = await getCompanyId();

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Upload pliku do Storage
      const storageRef = firebaseStorage.ref(`companies/${companyId}/offers/${offerId}/${targetFileName}`);
      await storageRef.put(file);
      const downloadURL = await storageRef.getDownloadURL();

      toastService.show('Dokument został załączony pomyślnie', 'Sukces', { variant: ToastVariant.SUCCESS });

      // Wywołaj callback
      await onUploadComplete(downloadURL);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toastService.show(error.message || 'Nie udało się załączyć dokumentu. Spróbuj ponownie.', 'Błąd uploadu', {
        variant: ToastVariant.ERROR,
      });
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isPDF = file.type === 'application/pdf';
    if (!isPDF) {
      toastService.show('Możesz załączyć tylko pliki PDF', 'Błąd', { variant: ToastVariant.ERROR });
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      toastService.show('Plik musi być mniejszy niż 10MB', 'Błąd', { variant: ToastVariant.ERROR });
      return false;
    }

    setFile(file);
    return false; // Prevent auto-upload
  };

  return (
    <Container>
      <Typography.Title level={3}>Podpis kwalifikowany wymagany</Typography.Title>

      <Alert
        type="warning"
        message="Umowa na czas oznaczony powyżej 12 miesięcy wymaga podpisu kwalifikowanego"
        description={
          <div>
            <p>
              Zgodnie z prawem, umowa najmu na czas oznaczony dłuższy niż rok powinna być zawarta w formie pisemnej
              (dokument z podpisem kwalifikowanym lub tradycyjny podpis).
            </p>
            <p style={{ marginTop: '10px' }}>
              <strong>Gdzie uzyskać podpis kwalifikowany?</strong> Możesz użyć aplikacji mObywatel (5 podpisów
              miesięcznie za darmo) lub innego dostawcy podpisu kwalifikowanego.
            </p>
          </div>
        }
        showIcon
      />

      <Card style={{ marginTop: '24px' }}>
        <Typography.Title level={4}>Instrukcja podpisywania</Typography.Title>
        <InstructionList>
          <li>
            <strong>Krok 1:</strong> Pobierz dokument umowy (kliknij przycisk poniżej)
          </li>
          <li>
            <strong>Krok 2:</strong> Podpisz dokument podpisem kwalifikowanym (np. używając aplikacji mObywatel lub
            innego dostawcy podpisu kwalifikowanego)
          </li>
          <li>
            <strong>Krok 3:</strong> Załącz podpisany dokument PDF poniżej
          </li>
        </InstructionList>

        <ButtonContainer>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
            size="large"
            block>
            1. Pobierz dokument do podpisu
          </Button>

          <Divider>NASTĘPNIE</Divider>

          <Upload
            beforeUpload={beforeUpload}
            accept="application/pdf"
            maxCount={1}
            showUploadList={true}
            onRemove={() => setFile(null)}>
            <Button icon={<UploadOutlined />} size="large" block disabled={uploading}>
              2. Wybierz podpisany dokument PDF
            </Button>
          </Upload>

          {file && (
            <>
              <Divider>ZAKOŃCZ</Divider>
              <Button
                type="primary"
                size="large"
                onClick={handleUpload}
                loading={uploading}
                block
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                {uploading ? <>Wysyłanie...</> : '3. Wyślij podpisany dokument i zakończ podpisywanie'}
              </Button>
            </>
          )}
        </ButtonContainer>
      </Card>

      <Alert
        type="info"
        message="Potrzebujesz pomocy?"
        description={
          <div>
            <p>
              Jeśli masz problem z podpisywaniem dokumentu podpisem kwalifikowanym, skontaktuj się z naszym zespołem
              wsparcia.
            </p>
          </div>
        }
        style={{ marginTop: '24px' }}
      />
    </Container>
  );
};
